/**
 * Manual Requirements Store
 * Manages user-defined equipment requirements per vessel
 * This replaces smart gates with manual control
 */

class ManualRequirementsStore {
  constructor() {
    this.cache = new Map();
  }

  /**
   * Get the storage key for a vessel
   */
  getStorageKey(vesselId) {
    return `sr.vessel.manualReqs.${vesselId}.v1`;
  }

  /**
   * Load requirements for a vessel
   */
  load(vesselId) {
    if (!vesselId) return [];
    
    // Check cache first
    if (this.cache.has(vesselId)) {
      return this.cache.get(vesselId);
    }

    const key = this.getStorageKey(vesselId);
    try {
      const stored = localStorage.getItem(key);
      const items = stored ? JSON.parse(stored) : [];
      this.cache.set(vesselId, items);
      return items;
    } catch (error) {

      return [];
    }
  }

  /**
   * Save requirements to storage
   */
  save(vesselId, items) {
    if (!vesselId) return;
    
    const key = this.getStorageKey(vesselId);
    try {
      localStorage.setItem(key, JSON.stringify(items));
      this.cache.set(vesselId, items);
    } catch (error) {

    }
  }

  /**
   * Add a new requirement
   */
  add(vesselId, item) {
    if (!vesselId || !item) return false;
    
    const items = this.load(vesselId);
    
    // Generate ID if not provided
    if (!item.id) {
      item.id = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    }
    
    // Add metadata if not present
    if (!item.createdAt) {
      item.createdAt = Date.now();
    }
    if (!item.addedBy) {
      item.addedBy = 'user';
    }
    if (!item.domain) {
      item.domain = 'FFE'; // Default to FFE for now
    }
    if (!item.status) {
      item.status = 'required';
    }
    
    // Check for duplicates
    const exists = items.some(i => i.id === item.id);
    if (exists) {

      return false;
    }
    
    items.push(item);
    this.save(vesselId, items);
    return true;
  }

  /**
   * Remove a requirement
   */
  remove(vesselId, itemId) {
    if (!vesselId || !itemId) return false;
    
    const items = this.load(vesselId);
    const filtered = items.filter(item => item.id !== itemId);
    
    if (filtered.length === items.length) {
      return false; // Nothing was removed
    }
    
    this.save(vesselId, filtered);
    return true;
  }

  /**
   * Update a requirement
   */
  update(vesselId, itemId, patch) {
    if (!vesselId || !itemId || !patch) return false;
    
    const items = this.load(vesselId);
    const index = items.findIndex(item => item.id === itemId);
    
    if (index === -1) {
      return false; // Item not found
    }
    
    // Apply patch
    items[index] = {
      ...items[index],
      ...patch,
      id: items[index].id, // Preserve ID
      updatedAt: Date.now()
    };
    
    this.save(vesselId, items);
    return true;
  }

  /**
   * Clear all requirements for a vessel
   */
  clear(vesselId) {
    if (!vesselId) return;
    
    const key = this.getStorageKey(vesselId);
    localStorage.removeItem(key);
    this.cache.delete(vesselId);
  }

  /**
   * Add basic FFE set (one-time seed)
   */
  seedBasicFFE(vesselId) {
    if (!vesselId) return false;
    
    const items = this.load(vesselId);
    
    // Check if already seeded
    const hasBasicFFE = items.some(item => item.seedSet === 'basicFFE');
    if (hasBasicFFE) {

      return false;
    }
    
    // Basic FFE items based on common requirements
    const basicFFE = [
      {
        id: 'ffe.extinguisher.portable.34b',
        domain: 'FFE',
        name: 'Portable Fire Extinguisher',
        spec: '34B Dry Powder',
        reference: 'WBC3 16.1.1',
        notes: 'Main deck and accommodation',
        status: 'required',
        addedBy: 'seed',
        seedSet: 'basicFFE',
        createdAt: Date.now()
      },
      {
        id: 'ffe.extinguisher.portable.13a',
        domain: 'FFE',
        name: 'Portable Fire Extinguisher',
        spec: '13A Water/Foam',
        reference: 'WBC3 16.1.1',
        notes: 'Accommodation spaces',
        status: 'required',
        addedBy: 'seed',
        seedSet: 'basicFFE',
        createdAt: Date.now()
      },
      {
        id: 'ffe.blanket.fire',
        domain: 'FFE',
        name: 'Fire Blanket',
        spec: 'Light duty 1.8m x 1.2m',
        reference: 'WBC3 16.1.2',
        notes: 'Galley area',
        status: 'required',
        addedBy: 'seed',
        seedSet: 'basicFFE',
        createdAt: Date.now()
      },
      {
        id: 'ffe.detector.smoke',
        domain: 'FFE',
        name: 'Smoke Detector',
        spec: 'Optical/Photoelectric',
        reference: 'WBC3 16.3',
        notes: 'Accommodation and wheelhouse',
        status: 'required',
        addedBy: 'seed',
        seedSet: 'basicFFE',
        createdAt: Date.now()
      },
      {
        id: 'ffe.bucket.fire',
        domain: 'FFE',
        name: 'Fire Bucket',
        spec: 'With lanyard',
        reference: 'WBC3 16.1.4',
        notes: 'Deck area',
        status: 'required',
        addedBy: 'seed',
        seedSet: 'basicFFE',
        createdAt: Date.now()
      }
    ];
    
    // Add all basic items
    basicFFE.forEach(item => {
      this.add(vesselId, item);
    });
    
    return true;
  }

  /**
   * Get counts by status
   */
  getCounts(vesselId) {
    const items = this.load(vesselId);
    return {
      total: items.length,
      required: items.filter(i => i.status === 'required').length,
      optional: items.filter(i => i.status === 'optional').length
    };
  }

  /**
   * Clear cache for a vessel
   */
  clearCache(vesselId) {
    if (vesselId) {
      this.cache.delete(vesselId);
    } else {
      this.cache.clear();
    }
  }
}

// Export singleton instance
const manualRequirementsStore = new ManualRequirementsStore();
export default manualRequirementsStore;